
import React from 'react';
import ChatBubble from '@amzn/awsui-chat-components-console/chat-bubble';
import Alert from '@amzn/awsui-components-console/alert';
import FileTokenGroup from '@amzn/awsui-components-console/file-token-group';
import LiveRegion from '@amzn/awsui-components-console/live-region';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import SupportPromptGroup from '@amzn/awsui-chat-components-console/support-prompt-group';

import FeedbackDialog from './additional-info/feedback-dialog';
import { ChatBubbleAvatar, CodeViewActions, FeedbackActions } from './common-components';
import { getAuthors, fileTokenGroupI18nStrings, Message, supportPromptItems } from './config';
import ContentRenderer from './content-renderer';
import { RecommendationBubble } from 'src/components/RecommendationBubble';
import { SummaryEventBubble } from 'src/components/SummaryEventBubble';
import { SuggestedResponseBox } from 'src/components/SuggestedResponseBox';
import { UserInfo } from 'src/services/api';
import { useMetrics } from 'src/hooks/useMetrics';
import { METRICS_EVENTS, SUGGESTED_RESPONSE_CONTEXT } from 'src/constants';

import '../../styles/chat.scss';

export default function Messages({
                                   messages = [],
                                   setShowFeedbackDialog,
                                   addMessage,
                                   sessionId,
                                   onFeedbackSubmit,
                                   onActionClick,
                                   user,
                                   onNextActionClick,
                                 }: {
  messages: Array<Message>;
  setShowFeedbackDialog: (index: number, show: boolean, isPositive?: boolean) => void;
  addMessage: (index: number, message: Message) => void;
  sessionId: string | null;
  onFeedbackSubmit: (data: any) => Promise<any>;
  onActionClick?: (action: string, index: number) => void;
  user?: UserInfo;
  onNextActionClick?: (action: string) => void;
}) {
  const latestMessage: Message = messages[messages.length - 1];
  const AUTHORS = getAuthors(user);
  const { logEvent } = useMetrics();

  const promptText = supportPromptItems.map(item => item.text);

  return (
    <div className="messages" role="region" aria-label="Chat">
      <LiveRegion hidden={true} assertive={latestMessage?.type === 'alert'}>
        {latestMessage?.type === 'alert' && latestMessage.header}
        {latestMessage?.content}
        {latestMessage?.type === 'chat-bubble' &&
          latestMessage.supportPrompts &&
          `There are ${promptText.length} support prompts accompanying this message. ${promptText}`}
      </LiveRegion>

      {messages.map((message, index) => {
        if (message.type === 'alert') {
          return (
            <Alert
              key={'error-alert' + index}
              header={message.header}
              type="error"
              i18nStrings={{ errorIconAriaLabel: "Error" }}
              data-testid={'error-alert' + index}
            >
              {message.content}
            </Alert>
          );
        }

        if (message.type === 'recommendation') {
          return (
            <div key={`recommendation-${index}`} style={{ width: '100%' }}>
              <RecommendationBubble
                recommendation={message.recommendation}
                onActionClick={(action) => onActionClick?.(action, message.index || 0)}
                index={message.index || 0}
                showLoadingBar={message.recommendation?.showLoadingBar}
                onFeedbackSubmit={onFeedbackSubmit}
                sessionId={sessionId}
                userPermissions={user?.posixGroups || []}
              />
            </div>
          );
        }

        if (message.type === 'summaryEvent') {
          return (
            <div key={`summary-${index}`} style={{ width: '100%' }}>
              <SummaryEventBubble
                problemStatement={message.summaryEvent.problemStatement}
                problemStatementExt={message.summaryEvent.problemStatementExt}
                customer_facing_suggested_response={message.summaryEvent.customer_facing_suggested_response}
                relevantDetails={message.summaryEvent.relevantDetails}
                tools_used={message.summaryEvent.tools_used}
                onFeedbackSubmit={onFeedbackSubmit}
                sessionId={sessionId}
              />
            </div>
          );
        }

        const author = AUTHORS[message.authorId] || { type: 'user', name: 'User', initials: 'U' };
        const messageId = `${message.authorId}-${message.timestamp}-${index}`;

        return (
          <SpaceBetween size="xs" key={message.authorId + message.timestamp}>
            <ChatBubble
              avatar={<ChatBubbleAvatar {...author} loading={message.avatarLoading} />}
              ariaLabel={`${author.name} at ${message.timestamp}`}
              type={author.type === 'gen-ai' ? 'incoming' : 'outgoing'}
              hideAvatar={message.hideAvatar}
              showLoadingBar={message.usingTool}
              actions={
                message.actions === 'code-view' ? (
                  <CodeViewActions contentToCopy={message.contentToCopy || ''} messageId={messageId} />
                ) : message.actions === 'feedback' ? (
                  <FeedbackActions
                    contentToCopy={message.contentToCopy || ''}
                    onFeedbackClick={(isPositive) => setShowFeedbackDialog(index, true, isPositive)}
                    userMessage={index > 0 ? String(messages[index - 1]?.content || '') : ''}
                    aiResponse={String(message.content || '')}
                    sessionId={sessionId}
                    messageId={messageId}
                  />
                ) : null
              }
            >
              <SpaceBetween size="xs">
                <div key={message.authorId + message.timestamp + 'content'}>
                  <ContentRenderer 
                    content={message.content} 
                    isMarkdown={author.type === 'gen-ai'} 
                  />
                </div>
                {author.type === 'gen-ai' && message.type === 'chat-bubble' && (message.suggestedResponse || (message.nextBestActions && message.nextBestActions.length > 0)) && (
                  <SpaceBetween size="s">
                    {message.suggestedResponse && (
                      <SuggestedResponseBox 
                        suggestedResponse={message.suggestedResponse}
                        onCopy={() => logEvent(METRICS_EVENTS.COPY_CONVERSE_SUGGESTED_RESPONSE)}
                        onFeedbackSubmit={onFeedbackSubmit}
                        sessionId={sessionId}
                        userMessage={SUGGESTED_RESPONSE_CONTEXT.CONVERSE}
                      />
                    )}
                    {message.nextBestActions && message.nextBestActions.length > 0 && (
                      <SupportPromptGroup
                        alignment="horizontal"
                        onItemClick={({ detail }) => {
                          const action = message.nextBestActions!.find((_, i) => `action-${i}` === detail.id);
                          if (action && onNextActionClick) {
                            onNextActionClick(action);
                          }
                        }}
                        ariaLabel="Next best actions"
                        items={message.nextBestActions.map((action, i) => ({
                          text: action,
                          id: `action-${i}`
                        }))}
                      />
                    )}
                  </SpaceBetween>
                )}
                {message.files && message.files.length > 0 && (
                  <FileTokenGroup
                    readOnly
                    items={message.files.map(file => ({ file }))}
                    limit={3}
                    onDismiss={() => {
                      /* empty function for read only token */
                    }}
                    alignment="horizontal"
                    showFileThumbnail={true}
                    i18nStrings={fileTokenGroupI18nStrings}
                  />
                )}
              </SpaceBetween>
            </ChatBubble>

            {message.showFeedbackDialog && (
              <div className="other-content-vertically-align">
                <FeedbackDialog
                  onDismiss={() => setShowFeedbackDialog(index, false)}
                  onSubmit={async (data) => {
                    await onFeedbackSubmit(data);
                    setShowFeedbackDialog(index, false);
                    addMessage(index + 1, {
                      type: 'chat-bubble',
                      authorId: 'gen-ai',
                      content: 'Your feedback has been submitted. Thank you for your feedback.',
                      timestamp: new Date().toLocaleTimeString(),
                      hideAvatar: true,
                    });
                  }}
                  userMessage={index > 0 ? String(messages[index - 1]?.content || '') : ''}
                  aiResponse={String(message.content || '')}
                  sessionId={sessionId}
                  isPositive={message.isPositiveFeedback || false}
                />
              </div>
            )}

            {latestMessage.type === 'chat-bubble' && latestMessage.supportPrompts && index === messages.length - 1 && (
              <div className="other-content-vertically-align">{message.supportPrompts}</div>
            )}
          </SpaceBetween>
        );
      })}
    </div>
  );
}
